/* eslint-disable @typescript-eslint/naming-convention */
/* eslint-disable no-underscore-dangle */
/* eslint-disable no-console */
import React, {
  createContext,
  useContext,
  useRef,
  useState,
  useCallback,
  useMemo,
  useEffect,
} from 'react';
import type { Dropin, PaymentMethodPayload } from 'braintree-web-drop-in';
import dropin from 'braintree-web-drop-in';
import { GOOGLE_PAY_MERCHANT_ID } from '@/constants/Config';
import { useDebouncedCallback } from '@/hooks/useDebouncedCallback';
import { usePublicRouteInfo } from '@/hooks/usePublicRouteInfo';

interface BraintreeContextProps {
  initializeDropIn: (
    authorization: string,
    containerId: string,
    showVenmoMethod?: boolean,
    chooseAWayToPay?: string,
    otherWaysToPay?: string,
    consentGiven?: boolean,
    vaultOptInOnly?: boolean
  ) => Promise<void>;
  requestPaymentMethod: () => Promise<PaymentMethodPayload>;
  isDropInInitialized: boolean;
  error: string | null;
  resetDropIn: (authorization: string, containerId: string, amount: string) => Promise<void>;
  validateDropIn: () => Promise<boolean>;
  isFormValid: boolean;
  updateAmount: (newAmount: string) => void;
  isDropInScreenLoading: boolean;
  getDropInStatus: () => boolean;
  selectedPaymentMethod: string | null;
  resetFormValidation: () => void;
  teardownDropIn: () => Promise<void>;
  setDropinConfig: (config: {
    authorization: string;
    containerId: string;
    amountToPay: string;
  }) => void;
}

interface BraintreeDropin extends Dropin {
  _dropinWrapper?: HTMLElement;
}

const BraintreeContext = createContext<BraintreeContextProps | undefined>(undefined);

const useBraintree = (): BraintreeContextProps => {
  const context = useContext(BraintreeContext);
  if (!context) {
    throw new Error('useBraintree must be used within a BraintreeProvider');
  }
  return context;
};

const BraintreeProvider = ({ children }: { children: React.ReactNode }) => {
  const dropinInstance = useRef<BraintreeDropin | null>(null);
  const isInitializing = useRef<boolean>(false);
  const [isDropInInitialized, setIsDropInInitialized] = useState<boolean>(false);
  const [isFormValid, setIsFormValid] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [amount, setAmount] = useState('0.00');
  const [isDropInScreenLoading, setIsDropInScreenLoading] = useState<boolean>(false);
  const currentConfig = useRef({ authorization: '', containerId: '', amountToPay: '' });
  const vaultOptInOnlyRef = useRef(false);
  const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<string | null>(null);
  const { isSpecificPublicRoute } = usePublicRouteInfo();
  const isPublicInvoicePaymentRoute = isSpecificPublicRoute('INVOICE_PAYMENT');

  const teardownDropIn = useCallback(async () => {
    if (dropinInstance.current) {
      try {
        /** Check if the Drop-in wrapper element exists directly */
        if (dropinInstance?.current?._dropinWrapper) {
          const wrapperElement = dropinInstance.current._dropinWrapper;
          if (!wrapperElement || !(wrapperElement instanceof HTMLElement)) {
            console.warn('Drop-in wrapper element is invalid or already removed from the DOM.');
            return;
          }
        }

        await dropinInstance.current.teardown();
        dropinInstance.current = null;
        setIsDropInInitialized(false);
        setSelectedPaymentMethod(null);
        setIsFormValid(false);
      } catch (err) {
        console.error('Error during Drop-in teardown:', err);
      }
    }
  }, []);

  const initializeDropIn = useCallback(
    async (
      authorization: string,
      containerId: string,
      showVenmoMethod?: boolean,
      chooseAWayToPay?: string,
      otherWaysToPay?: string,
      consentGiven?: boolean,
      vaultOptInOnly = false
    ): Promise<void> => {
      if (isInitializing.current) {
        console.warn('Drop-in is already initializing. Skipping redundant call.');
        return;
      }

      isInitializing.current = true;
      setIsDropInScreenLoading(true);
      vaultOptInOnlyRef.current = vaultOptInOnly;

      try {
        if (dropinInstance.current) {
          await teardownDropIn();
        }

        setSelectedPaymentMethod(null);
        setIsFormValid(false);

        const container = document.getElementById(containerId);
        if (!container) {
          throw new Error(`Container with ID '${containerId}' not found.`);
        }

        const venmoOptions = showVenmoMethod ? { allowNewBrowserTab: true } : undefined;

        const instance = await dropin.create({
          authorization,
          container: `#${containerId}`,
          vaultManager: vaultOptInOnly ? false : !isPublicInvoicePaymentRoute,
          paypal: { flow: consentGiven ? 'vault' : 'checkout', amount, currency: 'USD' },
          venmo: venmoOptions,
          googlePay: {
            googlePayVersion: 2,
            merchantId: GOOGLE_PAY_MERCHANT_ID,
            transactionInfo: {
              totalPriceStatus: 'FINAL',
              totalPrice: amount,
              currencyCode: 'USD',
            },
          },
          card: {
            cardholderName: { required: true },
            overrides: {
              styles: {
                input: {
                  fontSize: '16px',
                  color: '#474B6D',
                },
              },
              fields: {
                cvv: {
                  maskInput: true,
                },
              },
            },
            vault: {
              allowVaultCardOverride: vaultOptInOnly ? false : !isPublicInvoicePaymentRoute,
              ...((vaultOptInOnly || isPublicInvoicePaymentRoute) && { vaultCard: false }),
            },
          },
          dataCollector: {
            kount: true,
          },
          translations: {
            saveCardLabel: 'Save card info for faster checkout.',
            chooseAWayToPay: chooseAWayToPay ?? 'Pick a Payment Method',
            payWithCard: 'Pay via Card',
            otherWaysToPay: otherWaysToPay ?? 'Other ways to pay',
          },
        });

        dropinInstance.current = instance;

        currentConfig.current = {
          authorization,
          containerId,
          amountToPay: amount,
        };

        const saveCardCheckbox = document.querySelector(
          '[data-braintree-id="save-card-field-group"] input[type="checkbox"]'
        );

        if (saveCardCheckbox) {
          (saveCardCheckbox as HTMLInputElement).checked = false;
        }

        setIsDropInInitialized(true);

        /** Attach event listeners for form validation */
        instance.on('paymentMethodRequestable', () => setIsFormValid(true));
        instance.on('noPaymentMethodRequestable', () => setIsFormValid(false));

        if (instance.isPaymentMethodRequestable()) {
          setIsFormValid(true);
        }

        instance.on('paymentOptionSelected', (event) => {
          const selectedPaymentOption = event.paymentOption;
          setSelectedPaymentMethod(selectedPaymentOption);
        });
      } catch (err) {
        setError((err as Error).message || 'Failed to initialize Braintree Drop-in.');
      } finally {
        isInitializing.current = false;
        setIsDropInScreenLoading(false);
      }
    },
    [amount, isPublicInvoicePaymentRoute, teardownDropIn]
  );

  const resetDropIn = useCallback(
    async (authorization: string, containerId: string, amountToPay: string): Promise<void> => {
      if (
        currentConfig.current.authorization === authorization &&
        currentConfig.current.containerId === containerId &&
        currentConfig.current.amountToPay === amountToPay
      ) {
        console.log('Drop-in configuration unchanged. Skipping reset.');
        return;
      }

      currentConfig.current = { authorization, containerId, amountToPay };

      if (dropinInstance.current) {
        await dropinInstance.current.teardown();
        dropinInstance.current = null;
        setIsDropInInitialized(false);
      }

      await initializeDropIn(
        authorization,
        containerId,
        undefined,
        undefined,
        undefined,
        undefined,
        vaultOptInOnlyRef.current
      );
    },
    [initializeDropIn]
  );

  const debouncedResetDropIn = useDebouncedCallback(
    async (authorization: string, containerId: string, newAmount: string) => {
      await resetDropIn(authorization, containerId, newAmount);
    },
    300
  );

  const updateAmount = useCallback(
    (newAmount: string) => {
      setAmount(newAmount);

      if (currentConfig.current.authorization && currentConfig.current.containerId && newAmount) {
        debouncedResetDropIn(
          currentConfig.current.authorization,
          currentConfig.current.containerId,
          newAmount
        );
      }
    },
    [debouncedResetDropIn]
  );

  const setDropinConfig = useCallback(
    (config: { authorization: string; containerId: string; amountToPay: string }) => {
      currentConfig.current = { ...config };
    },
    []
  );

  useEffect(() => {
    const chooseAnotherWayButton = document.querySelector('[data-braintree-id="toggle"]');

    if (chooseAnotherWayButton) {
      const handleChooseAnotherWayClick = () => {
        setSelectedPaymentMethod(null);
      };

      chooseAnotherWayButton.addEventListener('click', handleChooseAnotherWayClick);

      return () => {
        chooseAnotherWayButton.removeEventListener('click', handleChooseAnotherWayClick);
      };
    }

    return undefined;
  }, [isDropInInitialized]);

  const getDropInStatus = useCallback(() => !!dropinInstance.current, []);

  const validateDropIn = useCallback(async (): Promise<boolean> => {
    if (dropinInstance.current) {
      const isValid = dropinInstance.current.isPaymentMethodRequestable();
      setIsFormValid(isValid);
      return isValid;
    }
    return false;
  }, []);

  const requestPaymentMethod = useCallback(async (): Promise<PaymentMethodPayload> => {
    if (dropinInstance.current) {
      return new Promise((resolve, reject) => {
        dropinInstance.current?.requestPaymentMethod((err, payload) => {
          if (err) {
            reject(new Error(err instanceof Error ? err.message : String(err)));
          } else {
            resolve(payload);
          }
        });
      });
    }
    throw new Error('Drop-in is not initialized.');
  }, []);

  const resetFormValidation = useCallback(() => {
    setIsFormValid(false);
  }, []);

  const contextValue = useMemo(
    () => ({
      initializeDropIn,
      requestPaymentMethod,
      isDropInInitialized,
      error,
      isFormValid,
      validateDropIn,
      resetDropIn,
      updateAmount,
      isDropInScreenLoading,
      getDropInStatus,
      selectedPaymentMethod,
      resetFormValidation,
      teardownDropIn,
      setDropinConfig,
    }),
    [
      initializeDropIn,
      requestPaymentMethod,
      isDropInInitialized,
      error,
      resetDropIn,
      isFormValid,
      validateDropIn,
      updateAmount,
      isDropInScreenLoading,
      getDropInStatus,
      selectedPaymentMethod,
      resetFormValidation,
      teardownDropIn,
      setDropinConfig,
    ]
  );

  useEffect(
    () => () => {
      teardownDropIn();
    },
    [teardownDropIn]
  );

  return <BraintreeContext.Provider value={contextValue}>{children}</BraintreeContext.Provider>;
};

export { BraintreeProvider, useBraintree };
